import * as React from "react";
import { useState } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { GridColDef } from "@mui/x-data-grid";

import AedasLoading from "../AedasLoading/AedasLoading";
import CustomModal from "../ModalCustom/CustomModal";
import ConfirmModal from "../ModalCustom/ConfirmModal";
import SectionHeader from "../SectionHeader/SectionHeader";
import { actionsColumn } from "../TableActions/TableActions";
import { ListEditItems } from "../../ListEditItems/ListEditItems";
import TableFilters from "../TableFilters/TableFilters";
import { FilterDef } from "../TableFilters/filterDefs";
import { useRowFilter } from "../TableFilters/useRowFilter";
import "./EntitySection.css";

// Página común de todas las secciones (Licencias, Activos, Dominios,
// Certificados, Partner): cabecera con "Añadir", tabla con acciones,
// ficha de información al hacer clic en una fila, edición en el formulario
// común (/editar/<tipo>/:id) y borrado con confirmación.

type EntitySectionProps = {
  title: string;
  addLabel: string;
  type: string; // tipo en las rutas del formulario común: /nuevo/<type>, /editar/<type>/:id
  rows: any[] | null;
  loading: boolean;
  columns: GridColDef[]; // sin la columna de acciones, que se añade aquí
  onDelete: (row: any) => Promise<any>;
  onChanged: () => void; // recargar los datos tras borrar
  detailTitle: string;
  renderDetail: (row: any) => React.ReactNode;
  filters?: FilterDef[]; // selectores propios de la sección, además del buscador
};

export default function EntitySection(props: EntitySectionProps) {
  const { title, addLabel, type, rows, loading, columns, onDelete, onChanged, detailTitle, renderDetail, filters = [] } = props;
  const history = useHistory();

  const [rowToDelete, setRowToDelete] = useState<any>(null);
  const [detailRow, setDetailRow] = useState<any>(null);

  // Buscador por texto (en las columnas visibles) y selectores de la sección
  const { filteredRows, filterProps } = useRowFilter(rows, columns, filters);

  const handleRemove = () => {
    if (!rowToDelete) return;
    onDelete(rowToDelete)
      .then(() => toast.success("Borrado"))
      .then(() => {
        setRowToDelete(null);
        onChanged();
      })
      .catch(() => toast.error("No se ha podido borrar, inténtelo de nuevo"));
  };

  const allColumns: GridColDef[] = [
    ...columns,
    actionsColumn({
      onEdit: (row) => history.push(`/editar/${type}/${row.id}`),
      onDelete: (row) => setRowToDelete(row),
    }),
  ];

  if (loading) {
    return (
      <div className="w-100 h-100 d-flex justify-content-center">
        <AedasLoading />
      </div>
    );
  }

  return (
    <div className="entityPage">
      <div className="main-container entityPage__card p-4">
        <SectionHeader title={title} addLabel={addLabel} addPath={`/nuevo/${type}`} />

        <ConfirmModal
          show={rowToDelete !== null}
          title="Confirmar borrado"
          message="¿Está seguro de que desea borrar este elemento? Esta acción no se puede deshacer."
          confirmLabel="Borrar"
          onConfirm={handleRemove}
          onCancel={() => setRowToDelete(null)}
        />

        <CustomModal
          size="xl"
          show={detailRow !== null}
          onChange={() => setDetailRow(null)}
          title={detailTitle}
          body={detailRow ? renderDetail(detailRow) : null}
        />

        <TableFilters {...filterProps} />

        <div className="entityPage__table">
          <ListEditItems rows={filteredRows} columns={allColumns} onRowClick={setDetailRow} fillHeight />
        </div>
      </div>
    </div>
  );
}
